import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';

import Loadable, { NullLoader } from '@comeon/loadable';

import { selectLicense } from '~/components/brand/ducks/selectors';
import { LICENSE_DEGA, LICENSE_KSA, LICENSE_OGA, LICENSE_SGA } from '~/utils/license-utils';

import RegulationsCommon from './regulations-common';

const RegulationsContainerDEGA = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "dega-bundle" */
			'./regulations-container-dega'
		),
	loading: NullLoader
});

const RegulationsContainerSGA = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "sga-bundle" */
			'./regulations-container-sga'
		),
	loading: NullLoader
});

const RegulationsContainerOGA = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "oga-bundle" */
			'./regulations-container-oga'
		),
	loading: NullLoader
});

const RegulationsContainerKSA = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "ksa-bundle" */
			'./regulations-container-ksa'
		),
	loading: NullLoader
});

type RegulationsContainerMapType = {
	[license: string]: FC<{ children: ReactNode }>;
};

const RegulationsContainerMap: RegulationsContainerMapType = {
	[LICENSE_DEGA]: RegulationsContainerDEGA,
	[LICENSE_SGA]: RegulationsContainerSGA,
	[LICENSE_OGA]: RegulationsContainerOGA,
	[LICENSE_KSA]: RegulationsContainerKSA
};

const NoRegulations: FC<{ children: ReactNode }> = ({ children }) => <>{children}</>; // eslint-disable-line react/jsx-no-useless-fragment

/**
 * Provides implementations for regulatory and license specific requirements.
 * Such an implementation introduce new elements or even alter the UIContext
 * in order to block or hide specific parts of the view.
 * Therefore it is necessary to wrap all visibile elements.
 * @param {children} children
 */
const RegulationsContainer: FC<{ children: ReactNode }> = ({ children }) => {
	const license = useSelector(selectLicense);
	const Container = RegulationsContainerMap[license] ?? NoRegulations;
	return (
		<>
			<RegulationsCommon />
			<Container>{children}</Container>
		</>
	);
};

export default RegulationsContainer;
